/**
 * Declared Value Protection (DVP) — the priced coverage option.
 *
 * PURE (no I/O), and mirrored byte-for-byte in frontend/src/lib/dvp.ts so the
 * figure a rep sees on the lead, the figure the contract prints, and the figure
 * the invoice line carries are the same number computed the same way. This is
 * the rule lib/estimate.ts already follows; a second implementation of a premium
 * is a second answer to "what did the client agree to pay".
 *
 * ---------------------------------------------------------------------------
 * THE PRODUCT
 * ---------------------------------------------------------------------------
 * (Every figure below is the DEFAULT. All five live on the company row and are
 * edited in Settings — see DVP_DEFAULTS.)
 * Every move includes Enhanced Coverage at no charge — $1.00/lb per article on
 * long distance, $0.60/lb local. That is what a client gets if they decline, and
 * it is not something they buy. DVP sits on top of it:
 *
 *   minimum declared value = billable weight x $6.00/lb, floored at $6,000
 *   premium                = max(declared value / 1000 x $18.00, $450)  (PRO-RATA
 *                            above the floor; the floor only ever raises)
 *   deductible             = $500 per claim
 *
 * Liability is capped at the declared value or the item's depreciated Actual
 * Cash Value, WHICHEVER IS LESS. It is never full or replacement coverage, and
 * it is never described as either — see the naming note at the bottom of this
 * file.
 *
 * ---------------------------------------------------------------------------
 * WHY PRO-RATA AND NOT PER STARTED THOUSAND
 * ---------------------------------------------------------------------------
 * The trade convention is to round the declared value up to the next whole
 * $1,000 before pricing. We do not, on instruction, and the reason is worth
 * keeping: the contract PRINTS the declared value, and a premium computed from
 * a silently rounded-up figure would not reconcile against the number on the
 * page the client signed. $72,390 declared quotes $1,049.66, not $1,058.50.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS A FLOOR UNDER THE PREMIUM
 * ---------------------------------------------------------------------------
 * Pure pro-rata prices a small shipment below what the coverage costs to carry:
 * the declared-value minimum is weight x $6.00/lb, so a 2,000 lb job declares
 * $12,000 and would pay $216 at $18.00 per $1,000 — for a policy with the same
 * administrative and claims exposure as a large one. dvp_min_premium raises
 * those and does nothing else; above it the price is still strictly pro-rata.
 *
 * At the default rates the floor binds below a $25,000 declared value, which is
 * a shipment under roughly 4,167 lbs — so on small long-distance jobs it is the
 * normal case, not the edge case. The contract must therefore state the premium
 * AS COMPUTED, floor included: quoting the pro-rata figure and invoicing the
 * floor would be quoting a price we do not charge.
 *
 * ---------------------------------------------------------------------------
 * WHY THE WEIGHT IS billableWeight AND NOT weight_lbs
 * ---------------------------------------------------------------------------
 * weight_lbs defaults to 0 and nothing requires it before a contract is
 * generated, so pricing off it directly quotes a $0 minimum on any lead nobody
 * has weighed. billableWeight (estimate.ts) is the floored figure the codebase
 * already treats as canonical — "displayed = stored = billed" — so DVP prices
 * off the same weight the transportation charge does.
 *
 * A SIDE EFFECT WORTH KNOWING: leads.minimum_weight defaults to 2000, so the
 * minimum declared value is normally 2000 x $6 = $12,000 and the $6,000 floor
 * never binds. The floor is a safety net for a branch running a minimum weight
 * under 1,000 lbs, not a live rule.
 */

import { billableWeight, EstimateLead } from './estimate';
import { isLongDistance } from './moveType';

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** The company-level rates (companies.dvp_*, migration 034). */
export interface DvpRates {
  dvp_min_rate_per_lb?: number | string | null;
  dvp_min_declared_value?: number | string | null;
  dvp_premium_per_1000?: number | string | null;
  dvp_min_premium?: number | string | null;
  dvp_deductible?: number | string | null;
}

export interface DvpLead extends EstimateLead {
  move_type?: string | null;
  declared_value?: number | string | null;
  dvp_premium_quoted?: number | string | null;
  dvp_elected_at?: string | null;
  /** 061: a fixed premium the office agreed (null = calculated). */
  dvp_premium_override?: number | string | null;
}

/**
 * The defaults, matching the column defaults in migrations 034 and 035.
 *
 * These exist so a company row that predates the migration — or one read back
 * before the PostgREST schema cache was reloaded — quotes the correct figures
 * rather than $0. They are NOT the source of truth: the columns are. A
 * hardcoded rate that silently wins over configuration is the failure D1/A11
 * fixed for the card fee, so these only ever fill an absence.
 */
export const DVP_DEFAULTS = {
  minRatePerLb: 6.0,
  minDeclaredValue: 6000,
  // 035 moved the COLUMN default from 14.50 to 18.00 for new companies and
  // deliberately left existing rows alone, so a configured company may well
  // read lower than this. That is correct: this value is only ever reached when
  // there is no configured rate to read at all.
  premiumPer1000: 18.0,
  minPremium: 450,
  deductible: 500,
} as const;

export interface DvpQuote {
  /** Is DVP offered on this lead at all? Long distance only. */
  offered: boolean;
  /** billableWeight — the weight the minimum is computed from. */
  weight: number;
  /** max(weight x rate, floor) — the rep may go up from here, never below. */
  minimum: number;
  /** What is actually declared: the lead's figure, clamped up to the minimum. */
  declaredValue: number;
  /** What is actually charged: the office's fixed premium (061) when there is
   *  one, else max(pro-rata, minPremium), rounded to cents. */
  premium: number;
  /** max(pro-rata, minPremium) — the premium the rates give, override or not. */
  calculatedPremium: number;
  /** True when premium is the office's fixed figure. Never repriced at the
   *  final scaled weight. */
  overridden: boolean;
  /** The minimum premium per contract. */
  minPremium: number;
  /** True when the floor is what set the price — the pro-rata figure was lower.
   *  Never true on an overridden premium. */
  premiumAtMinimum: boolean;
  deductible: number;
  /** Has the client accepted? Set from leads.dvp_elected_at. */
  elected: boolean;
  /** True when no declared value has been set on the lead yet (NULL, not 0). */
  unset: boolean;
}

const rateOf = (rates: DvpRates | null | undefined, key: keyof DvpRates, fallback: number): number => {
  const v = rates?.[key];
  const n = Number(v);
  // An absent, null, blank or non-numeric rate falls back. A stored 0 also
  // falls back: migration 034 forbids it, so a 0 arriving here is a bad read,
  // and honouring it would quote every client a $0 premium without erroring.
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

/**
 * A rate for which ZERO IS A REAL SETTING, not an absence.
 *
 * rateOf treats 0 as a bad read and falls back, which is right for the three
 * rates a zero would silently break. It is wrong here: a zero minimum premium
 * means "no minimum", which is exactly how this priced before 035, and falling
 * back to $450 would override a deliberate configuration with a default. Same
 * reasoning as deductibleFor below.
 */
const floorOf = (rates: DvpRates | null | undefined, key: keyof DvpRates, fallback: number): number => {
  const n = Number(rates?.[key]);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

/** Is DVP offered on this lead? LONG DISTANCE ONLY — local moves never see it. */
export function dvpOffered(lead: DvpLead | null | undefined): boolean {
  return isLongDistance(lead);
}

/** The minimum declared value: billable weight x rate, floored. */
export function minimumDeclaredValue(lead: DvpLead, rates?: DvpRates | null): number {
  const perLb = rateOf(rates, 'dvp_min_rate_per_lb', DVP_DEFAULTS.minRatePerLb);
  const floor = rateOf(rates, 'dvp_min_declared_value', DVP_DEFAULTS.minDeclaredValue);
  return round2(Math.max(billableWeight(lead) * perLb, floor));
}

/**
 * The premium for a declared value: pro-rata per $1,000, floored.
 *
 * The floor is applied to the ROUNDED pro-rata figure, so the comparison is
 * between the two numbers a person would actually see rather than between a
 * float and a price. At equality the pro-rata figure wins on its own merits —
 * max() makes that immaterial, which is the point of writing it this way.
 */
export function premiumFor(declaredValue: number | string | null | undefined, rates?: DvpRates | null): number {
  const per1000 = rateOf(rates, 'dvp_premium_per_1000', DVP_DEFAULTS.premiumPer1000);
  const floor = floorOf(rates, 'dvp_min_premium', DVP_DEFAULTS.minPremium);
  return Math.max(round2((num(declaredValue) / 1000) * per1000), round2(floor));
}

/** The pro-rata premium BEFORE the floor — what the rate alone would charge. */
export function proRataPremiumFor(declaredValue: number | string | null | undefined, rates?: DvpRates | null): number {
  const per1000 = rateOf(rates, 'dvp_premium_per_1000', DVP_DEFAULTS.premiumPer1000);
  return round2((num(declaredValue) / 1000) * per1000);
}

/** The minimum premium per contract. */
export function minPremiumFor(rates?: DvpRates | null): number {
  return round2(floorOf(rates, 'dvp_min_premium', DVP_DEFAULTS.minPremium));
}

/** The deductible per claim. */
export function deductibleFor(rates?: DvpRates | null): number {
  const v = Number(rates?.dvp_deductible);
  // 0 is a legitimate deductible (034 allows >= 0), so this one does not treat
  // it as an absence the way rateOf does.
  return Number.isFinite(v) && v >= 0 ? v : DVP_DEFAULTS.deductible;
}

/** The office's fixed premium (061), or null when the premium is calculated. */
export function premiumOverrideOf(lead: DvpLead | null | undefined): number | null {
  const v = lead?.dvp_premium_override;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? round2(n) : null;
}

/**
 * Everything about DVP on one lead, in one call. The clamp lives HERE rather
 * than at each call site: a declared value below the minimum is not a number to
 * reject, it is a number to raise, and doing that in one place is why the field,
 * the contract and the invoice line cannot disagree.
 */
export function dvpQuote(lead: DvpLead, rates?: DvpRates | null): DvpQuote {
  const minimum = minimumDeclaredValue(lead, rates);
  const stored = lead?.declared_value;
  const unset = stored == null || stored === '';
  const declaredValue = unset ? minimum : round2(Math.max(num(stored), minimum));
  const calculatedPremium = premiumFor(declaredValue, rates);
  const override = premiumOverrideOf(lead);
  return {
    offered: dvpOffered(lead),
    weight: billableWeight(lead),
    minimum,
    declaredValue,
    premium: override ?? calculatedPremium,
    calculatedPremium,
    overridden: override != null,
    minPremium: minPremiumFor(rates),
    premiumAtMinimum: override == null && proRataPremiumFor(declaredValue, rates) < minPremiumFor(rates),
    deductible: deductibleFor(rates),
    elected: !!lead?.dvp_elected_at,
    unset,
  };
}

/**
 * THE NAME, IN ONE PLACE.
 *
 * It is "Declared Value Protection", never "full" and never "replacement"
 * coverage. That is not house style — it is what the product is: liability is
 * capped at the declared value OR the depreciated Actual Cash Value, whichever
 * is less, so an item is not made whole and calling it replacement coverage
 * would misdescribe the cover in the document a client signs.
 *
 * Every user-visible string naming it comes from here, so it cannot drift back
 * one label at a time.
 */
export const DVP_NAME = 'Declared Value Protection';
export const DVP_ABBREV = 'DVP';

/**
 * The free base coverage a long-distance client keeps if they decline.
 *
 * LONG DISTANCE ONLY, and that is why it can be a constant here: local moves
 * carry $0.60/lb (a deliberate split — see the A1 note in
 * routes/templateContent.ts) and never see DVP at all, so this string is never
 * rendered on a local document.
 */
export const ENHANCED_LD_RATE_LABEL = '$1.00/lb per article';

/**
 * THE TWO OPTION LABELS, BUILT IN ONE PLACE.
 *
 * These strings are not decoration. Each one is stored verbatim in
 * document_choices.selected_option when the client picks it, quoted verbatim
 * into the activity feed, and read back by isDvpAcceptance() to decide whether
 * coverage was bought. Building them anywhere else would let the label the
 * client clicked and the label the system matches on drift apart, and the
 * failure would be silent: an accepted election that never flags the lead.
 */
export function dvpAcceptOption(q: { declaredValue: number; premium: number }): string {
  return `Accept ${DVP_NAME} — ${dvpMoney(q.declaredValue)} declared, premium ${dvpMoney(q.premium)}`;
}

export function dvpDeclineOption(): string {
  return `Decline — keep Enhanced Coverage at ${ENHANCED_LD_RATE_LABEL} at no charge`;
}

/**
 * Did this recorded selection BUY the coverage?
 *
 * Matches on the leading verb rather than the whole string, because the rest of
 * the accept label carries the amounts — which differ per lead and change if a
 * contract is regenerated at a new declared value. Matching the full string
 * would make a perfectly valid historical election read as a decline.
 *
 * Anything that is not recognisably an acceptance is treated as a DECLINE. That
 * is the safe direction: the failure mode is a client who bought coverage not
 * being flagged (visible, correctable), never a client who declined being
 * invoiced a premium and handed terms they refused.
 */
export function isDvpAcceptance(selected: string | null | undefined): boolean {
  return String(selected ?? '').trim().toLowerCase().startsWith('accept ' + DVP_NAME.toLowerCase());
}

/**
 * The premium line's description on the estimate. It starts with DVP_NAME,
 * which is what the invoice reprice (invoiceDraft) and the commission base
 * (commission.ts) find the premium by.
 */
export function dvpLineDescription(declaredValue: number): string {
  return `${DVP_NAME} — ${dvpMoney(declaredValue)} declared value`;
}

/**
 * A custom line that LOOKS like a valuation charge but is not the managed
 * premium line (061): typed by hand ("Valuation Coverage (…)") or added by the
 * old "+ Add DVP premium" button. The Coverage section offers to convert one
 * rather than add a second premium.
 */
export function isValuationLikeLine(description: string | null | undefined): boolean {
  return /valuation|declared value|\bdvp\b/i.test(String(description ?? ''));
}

/** The money format the documents and the CRM both use. */
export const dvpMoney = (v: unknown): string => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '$0.00';
  return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};


/**
 * SECTION 2A — the Declared Value Protection liability terms, VERBATIM.
 *
 * NUMBERED 2A, NOT 2. The long-distance contract already has a Section 2 (the
 * general limitation of liability, whose (A) covers the Enhanced base and whose
 * (B) points here). Two headings both reading "Section 2" on one contract is the
 * kind of ambiguity that gets argued over in a claim, and renumbering the
 * existing section was not an option: 1(A) cites "Section 2" by name and the
 * 2.1.x subsections hang off it. 2A subordinates cleanly to what is already
 * there and leaves every existing citation correct.
 *
 * THE INTERNAL NUMBERING IS AS SUPPLIED. Clauses 1-6 are Daryl's wording,
 * unchanged. Clause 7 (customer-packed containers) was added afterwards and is
 * the Section 2A counterpart of contract clause 3.2.1.3 — the two are written to
 * say the same thing, because a term that appears in one place and not the other
 * is a term a client can reasonably say they were not shown.
 *
 * WHY THE POLICY WORDING IS HERE AND NOT IN routes/templateContent.ts, where
 * every other clause lives: templateContent is a backend routes file, and this
 * text is rendered by templateEngine.ts, which is mirrored into the frontend for
 * the editor preview. Importing templateContent from the engine would make the
 * engine unmirrorable. dvp.ts is already mirrored and already owns the naming,
 * so the wording travels with it.
 *
 * The substance is Daryl's, unchanged. Only the markup is ours: an <ol> carries
 * the numbering that the source text states inline (1., 2., …), and the two
 * declared figures use the same ruled table the rest of the documents use. That
 * is the same rule templateContent states for all six templates — formatting
 * and structure improved, substance untouched.
 *
 * The values are substituted HERE rather than left as {{...}} placeholders,
 * because a dynamic block's output is not re-scanned by the merge pass.
 */
export function dvpSectionTwoAHtml(declaredValue: number, deductible: number): string {
  const esc = (v: unknown): string =>
    String(v ?? '').replace(/[&<>"]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m] as string));
  return `<h2>Section 2A: ${DVP_NAME} and Carrier Liability</h2>
<ol class="dvp-terms-list">
  <li><p><strong>Basis of Liability.</strong> Liability for loss or damage to the Customer's shipment is governed by the total declared value specified by the Customer in this agreement. The Company's liability shall not exceed the declared value or the Actual Cash Value of the goods, whichever is less, subject to the terms and depreciation rules below.</p>
    <table class="doc-addr"><tbody>
      <tr><th>Total Declared Value</th><td>${esc(dvpMoney(declaredValue))}</td></tr>
      <tr><th>Deductible per claim</th><td>${esc(dvpMoney(deductible))}</td></tr>
    </tbody></table></li>
  <li><p><strong>Actual Cash Value.</strong> In the event of total loss, destruction or irreparable damage to any article, the Company's maximum liability is limited to the Actual Cash Value of that item at the time of loss. Actual Cash Value means the replacement cost of the article less a reasonable deduction for depreciation, wear and tear, age and obsolescence. The Company is not liable for the original retail purchase price, sentimental value, or the cost of a brand-new replacement.</p></li>
  <li><p><strong>Right to Repair or Replace.</strong> In all cases of damage the Company may, at its sole option: pay the cost of professional repair to restore the item to its condition at pickup; replace the item with one of like kind, quality, age and condition; or offer a cash settlement based on the depreciated Actual Cash Value.</p></li>
  <li><p><strong>Pairs and Sets.</strong> Where one item of a matching pair or set is lost or damaged, the Company is liable only for the repair or depreciated value of that individual piece, not the value of the set.</p></li>
  <li><p><strong>Mechanical and Electronic Derangement.</strong> The Company is not liable for the internal mechanical, electronic or digital functionality of appliances, computers, musical instruments or electronics, unless there is visible external physical damage to the item's casing caused by the negligence of the Company's crew.</p></li>
  <li><p><strong>Articles of Extraordinary Value.</strong> Items whose value exceeds $100 per pound (including jewellery, currency, precious metals, furs, artwork and antiques) are not covered unless listed in writing on this agreement and accepted by the Company in advance.</p></li>
  <li><p><strong>Customer-Packed Containers.</strong> Where the Customer or the Customer's agent packed a container, this coverage applies only if the container shows visible external damage caused by the Company's handling. Where it does, the contents are covered under the declared value on the same terms as any other article, subject to the deductible above. Where there is no such visible external damage — including concealed damage discovered on unpacking — the article is settled at the Enhanced Coverage rate of $1.00 per pound per article rather than under the declared value. Containers packed by the Company are not subject to this clause. This mirrors clause 3.2.1.3 of the contract terms.</p></li>
</ol>`;
}

/**
 * BOTH OPTIONS, SIDE BY SIDE, PRICED.
 *
 * A client choosing between two levels of cover is comparing them, and prose
 * makes that work: the two prices end up in different paragraphs, at different
 * depths, and the free one reads as a preamble to the paid one rather than as an
 * equal choice. Two cards put the same facts in the same shape at the same size,
 * with the price as the headline of each, so the comparison is the layout rather
 * than something the reader has to assemble.
 *
 * WHICH IS NOT THE SAME AS SELLING. Enhanced Coverage is on the left, first,
 * with "Included" as its headline — because it is what the client already has
 * and what they keep by doing nothing. Neither card is tinted, boxed louder or
 * pre-ticked; the choicebox below them is where the decision is recorded, and it
 * starts empty.
 *
 * A TABLE, NOT FLEXBOX. This is printed through Chromium to PDF and the pair
 * must not break across a page. `display: table` with fixed layout is the
 * construct that has never surprised a print engine; flex and grid both have
 * fragmentation behaviour that varies by version, and a coverage comparison
 * split down the middle of a page break is a worse document than a slightly
 * plainer one.
 */
export function dvpOptionsHtml(q: {
  declaredValue: number; premium: number; deductible: number; weight: number;
  minPremium?: number; premiumAtMinimum?: boolean; overridden?: boolean;
}): string {
  // WHERE THE FLOOR SET THE PRICE, SAY SO. The premium is correct either way,
  // but a client reading "$12,000 declared" beside "$450" can work out the
  // per-$1,000 rate and find it does not reconcile. One line closes that, and on
  // a small long-distance shipment it is the usual case rather than a rare one.
  const floorNote = q.premiumAtMinimum
    ? `<p class="dvp-card-note">Minimum premium of ${dvpMoney(q.minPremium ?? 0)} per contract.</p>`
    : '';

  return `<p>Two levels of coverage are available on this shipment. <strong>${DVP_NAME} is optional.</strong> If you do not elect it, Enhanced Coverage applies automatically at no charge.</p>
<div class="dvp-options"><div class="dvp-options-row">
  <div class="dvp-card">
    <p class="dvp-card-name">Enhanced Coverage</p>
    <p class="dvp-card-price">Included<span class="dvp-card-unit">at no charge</span></p>
    <p class="dvp-card-rate">${ENHANCED_LD_RATE_LABEL}</p>
    <ul class="dvp-card-list">
      <li>Paid at ${ENHANCED_LD_RATE_LABEL.replace(' per article', '')} for the net weight of a lost or damaged article, regardless of the item's actual value.</li>
      <li>No deductible.</li>
      <li>Applies automatically unless you elect the option opposite.</li>
    </ul>
  </div>
  <div class="dvp-card">
    <p class="dvp-card-name">${DVP_NAME}</p>
    <p class="dvp-card-price">${dvpMoney(q.premium)}<span class="dvp-card-unit">premium</span></p>
    <p class="dvp-card-rate">${dvpMoney(q.declaredValue)} declared value</p>
    <ul class="dvp-card-list">
      <li>Liability limited to the declared value or the item's depreciated Actual Cash Value, whichever is less.</li>
      <li>${dvpMoney(q.deductible)} deductible per claim.</li>
      <li>Full terms in Section 2A.</li>
    </ul>
    ${floorNote}
  </div>
</div></div>
${q.overridden
    ? `<p class="doc-note">The declared value and premium shown are those agreed for this shipment. The premium is fixed and is not adjusted to the final scaled weight. Select <strong>one</strong> option below and sign your election.</p>`
    : `<p class="doc-note">The declared value shown is the minimum for an estimated ${q.weight.toLocaleString()} lbs. The premium is based on that estimated weight and is adjusted to the final scaled weight on your invoice. Select <strong>one</strong> option below and sign your election.</p>`}`;
}
